<script setup lang="ts">
import useLoading from '@/composables/useLoading';
import { useAppApi } from '@/plugins/appApi';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const t = useI18n().t;

const loading = ref(false);
const email = ref('');
const error = ref('');
const msg = ref('');

const handlePostError = (e: unknown) => {
  console.error(e);
  if ((e as { message?: string }).message === 'Failed to fetch') {
    error.value = t('recommendUs.errors.sendingFailed.network');
    return;
  }
  error.value = t('recommendUs.errors.sendingFailed');
};

const postRecommendation = async () => {
  const client = useAppApi();

  try {
    const res = await client.POST('/api/recommendations', {
      body: {
        email: email.value
      }
    });

    if (res.response.status == 400) {
      error.value = t('recommendUs.errors.requiredEmail');
      return;
    }

    if (!res.response.ok) {
      error.value = t('recommendUs.errors.sendingFailed');
      return;
    }

    msg.value = t('recommendUs.sent');
  } catch (e: unknown) {
    handlePostError(e);
  }
};

const isEmailSet = () => {
  if (!email.value) {
    error.value = t('recommendUs.errors.requiredEmail');
    return false;
  }

  return true;
};

const isValidEmail = () => {
  if (email.value.length < 3) {
    error.value = t('recommendUs.errors.invalidEmail');
    return false;
  }

  if (!email.value.includes('@')) {
    error.value = t('recommendUs.errors.invalidEmail');
    return false;
  }

  if (!email.value.includes('.')) {
    error.value = t('recommendUs.errors.invalidEmail');
    return false;
  }

  return true;
};

const create = () =>
  useLoading(loading, async () => {
    error.value = '';

    // Just very simple here for now...
    if (!isEmailSet() || !isValidEmail()) {
      console.log(error.value);
      return;
    }

    await postRecommendation();
  });
</script>

<template>
  <div class="flex flex-col px-4 pb-12">
    <img
      src="@/assets/recommendation-bg.png"
      class="h-[306px] w-full object-cover rounded-lg mb-10"
      alt="house-image"
    />
    <h1 class="text-2xl text-lumar-blue font-semibold mb-6">{{ $t('recommendUs.title') }}</h1>
    <p class="text-sm mb-8">
      {{ $t('recommendUs.hint') }}
    </p>
    <InputText
      v-model="email"
      class="w-full mb-6"
      :placeholder="$t('recommendUs.enterEmail')"
      type="email"
    />
    <Message v-if="error" severity="error" variant="simple">{{ error }}</Message>
    <Message v-if="msg" severity="success" variant="simple">{{ msg }}</Message>
    <br />
    <Button class="w-full" type="button" @click="create">
      <span class="text-white font-semibold">{{ $t('recommendUs.recommend') }}</span>
    </Button>
  </div>
</template>
